/** @jsx jsx */

import { Icon } from '@zapier/zinnia-icons';

import { PageNavigation, Spacer, Text } from '@zapier/design-system';
import { Spacing } from '@zapier/theme';
import { jsx, css } from '@emotion/core';

import Link from 'app/common/components/Link';
import { PARTNER_EMAIL_ADDRESS } from 'app/developer-v3/constants';
import { Status, StatusText, ZapTemplate } from 'app/zap-templates/types';

import CreateZapTemplateButton from './CreateZapTemplateButton';

const Styles = {
  getHelp: css`
    align-items: center;
    display: flex;
    gap: 1rem;
    margin: ${Spacing.small}px 0;
  `,
  infoLink: css`
    margin: 1rem 0;
  `,
  infoLinks: css`
    padding-left: ${Spacing.medium}px;
  `,
  root: css`
    display: flex;
    flex-direction: column;
    gap: ${Spacing.medium}px;
    height: 100%;
  `,
};

type Props = {
  onChangeStatus: (statusNext: Status) => void;
  status: Status | undefined;
  zapTemplates: ZapTemplate[];
};

function Sidebar({ onChangeStatus, status, zapTemplates }: Props) {
  const draftCount = zapTemplates.filter(
    zapTemplate => zapTemplate.status === Status.draft
  ).length;
  const forReviewCount = zapTemplates.filter(
    zapTemplate => zapTemplate.status === Status.forReview
  ).length;
  const publishedCount = zapTemplates.filter(
    zapTemplate => zapTemplate.status === Status.published
  ).length;
  const rejectedCount = zapTemplates.filter(
    zapTemplate => zapTemplate.status === Status.rejected
  ).length;
  const totalCount =
    draftCount + forReviewCount + publishedCount + rejectedCount;

  const items = [
    {
      href: '/zap-templates',
      icon: null,
      id: undefined,
      key: 'all',
      title: `All Partner Zap templates (${totalCount})`,
    },
    {
      href: `/zap-templates/${Status.draft}`,
      icon: null,
      id: Status.draft,
      key: Status.draft,
      title: `${StatusText[Status.draft]} (${draftCount})`,
    },
    {
      href: `/zap-templates/${Status.forReview}`,
      icon: null,
      id: Status.forReview,
      key: Status.forReview,
      title: `${StatusText[Status.forReview]} (${forReviewCount})`,
    },
    {
      href: `/zap-templates/${Status.rejected}`,
      icon: null,
      id: Status.rejected,
      key: Status.rejected,
      title: `${StatusText[Status.rejected]} (${rejectedCount})`,
    },
    {
      href: `/zap-templates/${Status.published}`,
      icon: null,
      id: Status.published,
      key: Status.published,
      title: `${StatusText[Status.published]} (${publishedCount})`,
    },
  ];

  return (
    <nav css={Styles.root}>
      <div>
        <div>
          <CreateZapTemplateButton />
        </div>
        <Spacer height={Spacing.medium} />
        <PageNavigation
          initiallySelected={status}
          items={items}
          onSelect={item => onChangeStatus(item.id as Status)}
          title="My Zap templates"
        />
        <div>
          <Text margin="32px 0 0" tag="p" type="Body3">
            About Zap templates
          </Text>
          <ul css={Styles.infoLinks}>
            <li css={Styles.infoLink}>
              <Text tag="p" type="Body2">
                <Link
                  href="https://docs.zapier.com/platform/publish/zap-templates"
                  target="_blank"
                >
                  What are Zap templates?
                </Link>
              </Text>
            </li>
            <li css={Styles.infoLink}>
              <Text tag="p" type="Body2">
                <Link
                  href="https://docs.zapier.com/platform/publish/zap-templates"
                  target="_blank"
                >
                  How to create Zap templates?
                </Link>
              </Text>
            </li>
            <li css={Styles.infoLink}>
              <Text tag="p" type="Body2">
                <Link
                  href="https://docs.zapier.com/platform/publish/zap-templates#5-submit-zap-template-for-review"
                  target="_blank"
                >
                  Submit your Zap templates
                </Link>
              </Text>
            </li>
            <li css={Styles.infoLink}>
              <Text tag="p" type="Body2">
                <Link href="https://zapier.com/partner/embed/" target="_blank">
                  Embedding your Zap templates
                </Link>
              </Text>
            </li>
          </ul>
        </div>
      </div>
      <div>
        <div css={Styles.getHelp}>
          <Icon name="commentBlankFill" size={20} /> Get Help
        </div>
        <Text>
          Email{' '}
          <Link href={`mailto:${PARTNER_EMAIL_ADDRESS}`}>
            {PARTNER_EMAIL_ADDRESS}
          </Link>{' '}
          for help with your Zap templates.
        </Text>
      </div>
    </nav>
  );
}

export default Sidebar;
